import React, { useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Canvas, useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { FaGithub, FaLinkedin } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';

const HeroContainer = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5%;
  background: linear-gradient(45deg, #0a192f, #112240);
  position: relative;
  overflow: hidden;
  gap: 5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 10% 5%;
    text-align: center;
    gap: 2rem;
  }
`;

const ContentContainer = styled.div`
  z-index: 1;
  color: #ffffff;
  max-width: 600px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
    align-items: center;
  }
`;

const ProfileImageContainer = styled(motion.div)`
  width: 350px;
  height: 350px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #64ffda;
  box-shadow: 0 0 20px rgba(100, 255, 218, 0.3);
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 250px;
    height: 250px;
    margin: 1rem auto;
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const Title = styled(motion.h1)`
  font-size: 4rem;
  margin-bottom: 1rem;
  background: linear-gradient(120deg, #64ffda, #00bcd4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.5rem;
  color: #8892b0;
  margin-bottom: 1.5rem;
`;

const Description = styled(motion.p)`
  font-size: 1.1rem;
  color: #8892b0;
  line-height: 1.8;
  margin-bottom: 2rem;
  max-width: 600px;
  text-align: justify;

  @media (max-width: 768px) {
    text-align: left;
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const ButtonContainer = styled(motion.div)`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    max-width: 300px;
  }
`;

const CTAButton = styled(motion.button)`
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: transform 0.2s ease;
  min-width: 150px;

  &:hover {
    transform: translateY(-2px);
  }

  &.primary {
    background: linear-gradient(120deg, #64ffda, #00bcd4);
    color: #0a192f;
  }

  &.secondary {
    background: transparent;
    border: 2px solid #64ffda;
    color: #64ffda;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CanvasContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 50%;
  height: 100%;
  opacity: 0.6;
`;

const SocialBar = styled.div`
  position: fixed;
  right: 40px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 100;

  @media (max-width: 768px) {
    right: 20px;
  }
`;

const SocialIcon = styled(motion.a)`
  color: #64ffda;
  font-size: 24px;
  cursor: pointer;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(100, 255, 218, 0.1);
  border: 1px solid #64ffda;

  &:hover {
    color: #ffffff;
    background: rgba(100, 255, 218, 0.2);
    transform: translateY(-3px);
  }
`;

const Circuit = ({ position, rotation }) => {
  const lineRef = useRef();
  const nodeRef = useRef();
  const speed = useMemo(() => Math.random() * 0.02 + 0.01, []);
  const length = useMemo(() => Math.random() * 2 + 1, []);
  const colorSpeed = useMemo(() => Math.random() * 0.001 + 0.001, []);
  const colorPhase = useMemo(() => Math.random() * Math.PI * 2, []);

  useFrame(({ clock }) => {
    if (lineRef.current && nodeRef.current) {
      // Animation de la longueur de la ligne
      lineRef.current.scale.x = (Math.sin(Date.now() * speed) + 1) * length;
      
      // Animation de couleur
      const time = clock.getElapsedTime();
      const hue = (Math.sin(time * colorSpeed + colorPhase) + 1) * 0.5;
      const color = new THREE.Color().setHSL(hue, 0.8, 0.6);
      const emissiveIntensity = (Math.sin(time * speed * 2) + 1.5) * 0.3;
      
      lineRef.current.material.color = color;
      lineRef.current.material.emissive = color;
      lineRef.current.material.emissiveIntensity = emissiveIntensity;
      
      nodeRef.current.material.color = color;
      nodeRef.current.material.emissive = color;
      nodeRef.current.material.emissiveIntensity = emissiveIntensity;
    }
  });

  return (
    <group position={position} rotation={rotation}>
      <mesh ref={nodeRef}>
        <boxGeometry args={[0.1, 0.1, 0.1]} />
        <meshStandardMaterial 
          color="#64ffda"
          emissive="#64ffda"
          emissiveIntensity={0.5}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>
      <mesh ref={lineRef} position={[length / 2, 0, 0]}>
        <boxGeometry args={[1, 0.05, 0.05]} />
        <meshStandardMaterial 
          color="#64ffda"
          emissive="#64ffda"
          emissiveIntensity={0.5}
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>
    </group>
  );
};

const BinaryText = ({ position }) => {
  const textRef = useRef();
  const colorRef = useRef({ hue: Math.random() });
  
  const binaryMessage = useMemo(() => {
    const messages = [
      "Hello World",
      "Portfolio",
      "Developer",
      "Creative"
    ];
    const message = messages[Math.floor(Math.random() * messages.length)];
    return message.split('').map(char => char.charCodeAt(0).toString(2).padStart(8, '0')).join('');
  }, []);

  useFrame(({ clock }) => {
    if (textRef.current) {
      const time = clock.getElapsedTime();
      
      // Animation de position
      textRef.current.position.y = position[1] + Math.sin(time + position[0]) * 0.2;
      
      // Animation d'opacité
      textRef.current.material.opacity = (Math.sin(time * 2 + position[2]) + 1) * 0.5;
      
      // Animation de couleur
      colorRef.current.hue += 0.001;
      if (colorRef.current.hue > 1) colorRef.current.hue = 0;
      
      const color = new THREE.Color().setHSL(
        colorRef.current.hue,
        0.8,
        0.6 + Math.sin(time * 3 + position[0]) * 0.2
      );
      
      textRef.current.material.color = color;
    }
  });

  return (
    <Text
      ref={textRef}
      position={position}
      fontSize={0.3}
      color="#64ffda"
      anchorX="center"
      anchorY="middle"
      material-transparent={true}
      material-metalness={0.8}
      material-roughness={0.2}
    >
      {binaryMessage.slice(Math.floor(position[0] * 10), Math.floor(position[0] * 10) + 8)}
    </Text>
  );
};

const CircuitScene = () => {
  const circuits = useMemo(() => {
    return Array.from({ length: 20 }, (_, i) => ({
      position: [
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 5
      ],
      rotation: [
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      ]
    }));
  }, []);

  const binaryPositions = useMemo(() => {
    return Array.from({ length: 15 }, (_, i) => [
      (Math.random() - 0.5) * 8,
      (Math.random() - 0.5) * 8,
      (Math.random() - 0.5) * 3
    ]);
  }, []);

  useFrame(({ camera, clock }) => {
    const time = clock.getElapsedTime();
    camera.position.x = Math.sin(time * 0.2) * 12;
    camera.position.z = Math.cos(time * 0.2) * 12;
    camera.lookAt(0, 0, 0);
  });

  return (
    <>
      <ambientLight intensity={0.3} />
      <pointLight position={[10, 10, 10]} intensity={0.8} color="#64ffda" />
      <pointLight position={[-10, -10, -10]} intensity={0.5} color="#00bcd4" />
      <fog attach="fog" args={['#0a192f', 8, 25]} />
      
      {circuits.map((circuit, i) => (
        <Circuit key={i} position={circuit.position} rotation={circuit.rotation} />
      ))}
      
      {binaryPositions.map((pos, i) => (
        <BinaryText key={i} position={pos} />
      ))}
    </>
  );
};

const iconVariants = {
  initial: { scale: 0, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  hover: { scale: 1.1 }
};

const Hero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <HeroContainer>
      <ContentContainer>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Title variants={itemVariants}>
            Bonjour, je suis <br />
            <span>Isaac Naranjo</span>
          </Title>
          <Subtitle variants={itemVariants}>
            Développeur, Full Stack passionné par la création d'expériences innovantes
          </Subtitle>
          <Description variants={itemVariants}>
            Depuis mon plus jeune âge, créer grâce à l'imagination a toujours été une passion qui m'a poussé à développer des compétences artistiques, notamment à travers le dessin. L'idée de pouvoir donner vie à n'importe quel concept en utilisant un ordinateur m'a tellement fasciné que j'ai décidé de plonger dans le monde de la technologie. Aujourd'hui, je canalise mon imagination dans le domaine numérique pour créer des projets innovants et uniques.
          </Description>
          <ButtonContainer variants={itemVariants}>
            <CTAButton 
              className="primary"
              onClick={() => scrollToSection('projects')}
            >
              Voir mes projets
            </CTAButton>
            <CTAButton 
              className="secondary"
              onClick={() => scrollToSection('contact')}
            >
              Me contacter
            </CTAButton>
          </ButtonContainer>
        </motion.div>
      </ContentContainer>

      <ProfileImageContainer
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        whileHover={{ scale: 1.02 }}
      >
        <ProfileImage 
          src="/uploads/profiles/IN_CV.jpeg" 
          alt="Photo de profil"
        />
      </ProfileImageContainer>

      <SocialBar>
        <SocialIcon 
          href="https://github.com/Arcadiastyx" 
          target="_blank"
          rel="noopener noreferrer"
          as={motion.a}
          variants={iconVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
          aria-label="GitHub"
        >
          <FaGithub />
        </SocialIcon>
        <SocialIcon 
          href="https://www.linkedin.com/in/isaac-naranjo/" 
          target="_blank"
          rel="noopener noreferrer"
          as={motion.a}
          variants={iconVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
          aria-label="LinkedIn"
        >
          <FaLinkedin />
        </SocialIcon>
        <SocialIcon 
          href="mailto:isaac.naranjo.e.g@gmail.com"
          as={motion.a}
          variants={iconVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
          aria-label="Email"
        >
          <MdEmail />
        </SocialIcon>
      </SocialBar>

      <CanvasContainer>
        <Canvas camera={{ position: [0, 0, 10], fov: 75 }}>
          <CircuitScene />
        </Canvas>
      </CanvasContainer>
    </HeroContainer>
  );
};

export default Hero;
